import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import { useHistory } from 'react-router-dom';
import NotificationTemplateFormInner from './NotificationTemplateFormInner';
import { templateFieldNameMap } from './NotificationTemplateForm/NotificationTemplateForm';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import useNotificationTemplate from '../../../hooks/useNotificationTemplate/useNotificationTemplate';
import {
  createNotificationTemplate,
  editNotificationTemplate,
  selectNotificationTemplateById,
  selectNotificationTemplatesCrudOperation,
  selectNotificationTemplatesCrudStatus,
  selectNotificationTemplatesErrors,
  setNotificationTemplateCrudType,
} from '../../../../redux/slices/notificationTemplatesSlice/notificationTemplatesSlice';
import FormButtonGroup from '../../../common/FormButtonGroup/FormButtonGroup';
import { pushSessionMessage } from '../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import { CrudType } from '../../../../utility/types/CrudType';
import { CrudStatus } from '../../../../utility/types/CrudStatus';

function NotificationTemplateFormLayout({ notificationTemplateId, clone }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const errorList = useSelector(selectNotificationTemplatesErrors);
  const crudOperation = useSelector(selectNotificationTemplatesCrudOperation);
  const crudStatus = useSelector(selectNotificationTemplatesCrudStatus);
  const prevCrudStatusRef = useRef(null);

  const initialTemplate = useSelector(
    (state) => selectNotificationTemplateById(state, notificationTemplateId),
  );
  const { activeSubscription } = useSelector(selectSessionInformation);
  const { activeTierHasAdvancedAlerting } = activeSubscription;

  const [cursorPosition, setCursorPosition] = useState(0);
  const [cursorFieldName, setCursorFieldName] = useState(
    templateFieldNameMap.TRIGGERED_EMAIL_SUBJECT,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    template,
    updateTemplate,
    resetTemplateByType,
  } = useNotificationTemplate({ initialTemplate, clone });

  const updateCursorProperties = (e) => {
    const { selectionStart, name } = e.target;
    setCursorPosition(selectionStart);
    setCursorFieldName(name);
  };

  const onFieldTextChange = (e) => {
    const { name, value } = e.target;
    updateCursorProperties(e);
    updateTemplate(name, value);
  };

  const insertKeywordAtCaret = (innerText) => {
    const { [cursorFieldName]: templateText } = template;
    const textBeforeCursorPosition = templateText.substring(0, cursorPosition);
    const textAfterCursorPosition = templateText
      .substring(cursorPosition, templateText.length);
    const newTemplateText = textBeforeCursorPosition + innerText + textAfterCursorPosition;
    updateTemplate(cursorFieldName, newTemplateText);
  };

  const handleSubmitNew = async () => {
    setIsSubmitting(true);
    await dispatch(createNotificationTemplate({ notificationTemplate: template })).unwrap();
    setIsSubmitting(false);
  };

  const handleSubmitEdited = async () => {
    setIsSubmitting(true);
    await dispatch(editNotificationTemplate({
      notificationTemplate: template,
      patchFields: [
        'name',
        'useDefaultEmailTemplate',
        'useDefaultSmsTemplate',
        'triggeredEmailSubject',
        'triggeredEmailBody',
        'triggeredSmsBody',
        'clearedEmailSubject',
        'clearedEmailBody',
        'clearedSmsBody',
      ],
    })).unwrap();
    setIsSubmitting(false);
  };

  const handleSaveSuccess = () => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_SUCCESS,
      title: 'Saved',
      body: 'Notification Template saved successfully.',
    }));

    dispatch(setNotificationTemplateCrudType(CrudType.READ));

    history.push('/s/actions/notification-templates');
  };

  const handleSaveError = () => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_ERROR,
      title: 'Save Error',
      body: 'Error saving Notification Template.',
    }));

    setIsSubmitting(false);
  };

  useEffect(() => {
    const prevCrudStatus = prevCrudStatusRef.current;
    prevCrudStatusRef.current = crudStatus;

    if ((crudOperation === CrudType.CREATE || crudOperation === CrudType.UPDATE)
      && crudStatus !== prevCrudStatus) {
      switch (crudStatus) {
        case CrudStatus.SUCCESS:
          handleSaveSuccess();
          break;
        case CrudStatus.ERROR:
          handleSaveError();
          break;
        default:
          break;
      }
    }
  }, [crudStatus, crudOperation]);

  return (
    <form>
      <h2 className="mb-3">Notification Template Form</h2>
      <NotificationTemplateFormInner
        template={template}
        errorList={errorList}
        onFieldTextChange={onFieldTextChange}
        updateCursorProperties={updateCursorProperties}
        insertKeywordAtCaret={insertKeywordAtCaret}
        updateTemplate={updateTemplate}
        resetTemplateByType={resetTemplateByType}
        activeTierHasAdvancedAlerting={activeTierHasAdvancedAlerting}
      />
      <FormButtonGroup
        onSubmit={(notificationTemplateId && !clone) ? handleSubmitEdited : handleSubmitNew}
        redirectLink="/s/actions/notification-templates"
        isSubmitting={isSubmitting}
        submitBtnText="Save Changes"
        loadingBtnText="Saving..."
      />
    </form>
  );
}

NotificationTemplateFormLayout.defaultProps = {
  notificationTemplateId: null,
  clone: false,
};

NotificationTemplateFormLayout.propTypes = {
  notificationTemplateId: PropTypes.number,
  clone: PropTypes.bool,
};
export default NotificationTemplateFormLayout;
